import { useEffect } from "react";
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { hydrateAuth, useAuthStore } from "../lib/authStore";
import { OfflineBanner } from "../components/custom/OfflineBanner";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

/**
 * Root layout — auth bootstrap + magic-link token consume guard.
 *
 * Responsibilities:
 *   1. Při mount fetchnout /api/auth/me a hydratovat `useAuthStore`. Outlet
 *      se renderuje až po dokončení (loading shellem se zaplní `defaultPendingComponent`
 *      v `createRouter`).
 *   2. REQ-AUTH-018 — pokud URL obsahuje `?token=...` query param (magic link
 *      consume z UC-003 / UC-006 invitation flow), zavoláme
 *      `history.replaceState()` po jeho přečtení, aby se token neuložil do
 *      browser history (mitigation pro R-013 / R-039).
 *   3. Per DEC-013 — pokud auth response signalizuje `requiresConsentRePrompt`,
 *      forced redirect na `/consent/re-prompt` (route guards na admin/tenant
 *      tomu zabraňují vstupu do shellu).
 *
 * Side-effect free render: nemá vlastní UI mimo `<Outlet />` — nested layouts
 * (admin/_layout, tenant/_layout) renderují Application Shell.
 */
export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });

  useKeyboardShortcuts();

  // Strip magic-link `?token` z URL po consume (REQ-AUTH-018).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.has("token")) {
      url.searchParams.delete("token");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }, [location.pathname]);

  // Hydrate /api/auth/me once on mount.
  useEffect(() => {
    let cancelled = false;
    useAuthStore.getState().setLoading();
    hydrateAuth().then((state) => {
      if (cancelled) return;
      // Re-prompt routing per DEC-013 Policy C.
      if (
        state.state === "authenticated" &&
        state.user.requiresConsentRePrompt &&
        !location.pathname.startsWith("/consent/re-prompt")
      ) {
        navigate({ to: "/consent/re-prompt" });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, location.pathname]);

  return (
    <>
      <OfflineBanner />
      <Outlet />
    </>
  );
}
